//================================================================
//  Page: ApprovalDetails
//================================================================

//  Purpose: Detailed Approval page - shows individual approval in detail

//================================================================

//Libraries
import { useContext, useEffect, useState, useReducer } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import SideNav from '../../../Components/SideNav/SideNav';
import BreadCrumbs from '../../../Components/Breadcrumbs/BreadCrumbs';
import TabView from '../../../Components/TabView/TabView';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import ActivityFeed from '../../../Components/ActivityFeed/ActivityFeed';
import FormRenderer from '../../../Components/FormRenderer/FormRenderer';
import UserSearchNative from '../../../Components/UserSearchNative/UserSearchNative';

//Functions
import DocumentListener from '../../../Library/DocumentListener';
import ConvertDate from '../../../Library/ConvertDate';
import WriteDocument from '../../../Library/WriteDocument';
import AddRemoveDocumentArray from '../../../Library/AddRemoveDocumentArray';

//Images
import Expand from '../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../Components/Images/Icon_Expand_Teal.svg';
import FileIcon from '../../../Components/Images/Icon_File_Grey.svg';

//CSS
import '../Account.css';

export default function ApprovalDetails() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const params = useParams();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Contains Firestore Document of the request
    const [requestDoc, setRequestDoc] = useState(undefined);

    // For changing request status dropdown > 'Approve', 'Reassign', 'Reject'
    const [requestStatus, setRequestStatus] = useState('');

    // Some status changes require an additional comment
    const [changeStatusComment, setChangeStatusComment] = useState('');

    // Used to toggle view of details
    const [viewDetails, setViewDetails] = useState(true);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'newUserInput': '', // email of the selected user
            'newUserName': '', // fullname of the selected user
            'newGivenname': '', // givenname of the selected user
            'newSurname': '', // surname of the selected user
            'newJobTitle': '', // jobtitle of the selected user
            'searchResults': '',
            'queryMessage': '',
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Disables the "Proceed" button if conditions aren't met
    function isSubmitDisabled() {

        if (getUser.emailaddress !== requestDoc?.assignedto.email) return true;

        // Enable button when dropdown option is selected
        if (requestStatus === 'Approve') return false;

        if (requestStatus === 'Reassign') 
            return searchData.newUserInput.length > 0 && changeStatusComment.length > 0 ? false : true;

        if (requestStatus === 'Reject') {
            return changeStatusComment.length > 0 ? false : true;
        }

        return true;

    }

    // Submits form
    function handleSubmit() {

        if (requestStatus === 'Approve') {

            const newActivityFeed = [...requestDoc.activityfeed, {
                'action': 'approved',
                'actionedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'activitydate': new Date(),
                'comments': changeStatusComment
            }]

            const docObject = {
                'status': 'Completed',
                'activityfeed': newActivityFeed,
                'lastmodifiedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'lastmodifieddate': new Date(),
                'comments': changeStatusComment,
            }

            return WriteDocument('requests', requestDoc?.requestid, docObject, true)
                .then(() => {

                    setRequestStatus('');
                    setChangeStatusComment('');

                })
                .catch((error) => {

                    console.log('error', error);
                    setPageStatus('error-fatal');

                })
        }

        if (requestStatus === 'Reject') {

            // Update the activity feed (array field)
            const newActivityFeed = {
                'action': 'rejected',
                'actionedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'activitydate': new Date(),
                'comments': changeStatusComment
            }

            const updateActivityFeedPromise = AddRemoveDocumentArray('requests', requestDoc?.requestid, 'activityfeed', newActivityFeed, 'add', newActivityFeed.actionedby);

            // Update status and other fields
            const docObject = {
                'status': 'Rejected',
                'lastmodifiedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'lastmodifieddate': new Date(),
                'rejectionreason': changeStatusComment,
            }

            const updateStatusPromise = WriteDocument('requests', requestDoc?.requestid, docObject, true);


            return Promise.all([updateActivityFeedPromise, updateStatusPromise])
                .then(() => {

                    setRequestStatus('');
                    setChangeStatusComment('');

                })
                .catch((error) => {

                    console.log('error', error);
                    setPageStatus('error-fatal');

                })
        }

        if (requestStatus === 'Reassign') {

            const newActivityFeed = [...requestDoc.activityfeed, {
                'action': 'unassigned',
                'actionedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'activitydate': new Date(),
                'comments': changeStatusComment
            }, 
            {
                'action': 'assigned',
                'actionedby': {
                    'email': searchData.newUserInput,
                    'givenname': searchData.newGivenname,
                    'surname': searchData.newSurname, 
                },
                'activitydate': new Date(),
                'comments': changeStatusComment
            }]

            const docObject = {
                'activityfeed': newActivityFeed,
                'assignedto': {
                    'email': searchData.newUserInput,
                    'givenname': searchData.newGivenname,
                    'surname': searchData.newSurname, 
                },
                'lastmodifiedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'lastmodifieddate': new Date(),
                'comments': changeStatusComment,
            }

            return WriteDocument('requests', requestDoc?.requestid, docObject, true)
                .then(() => {

                    setRequestStatus('');
                    setChangeStatusComment('');
                    setSearchData({
                        'newUserInput': '', // email of the selected user
                        'newUserName': '', // fullname of the selected user
                        'newGivenname': '', // givenname of the selected user
                        'newSurname': '', // surname of the selected user
                        'newJobTitle': '', // jobtitle of the selected user
                        'searchResults': '',
                        'queryMessage': '',
                    });    

                })
                .catch((error) => {

                    console.log('error', error);
                    setPageStatus('error-fatal');

                })
        }

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Get task document from Firestore
    useEffect(() => {

        const requestid = params.id;

        function onLoadChange(document) {

            const approvalTypeList = ['Initial Approval', 'Commercial Second Round Approval', 'Final Approval', 'Finance Approval', 'COO Approval', 'LoA Approval', 'Other Approval', 'Quote Review', 'Budget Owner (LoA) Approval', 'Finance Manager Approval']
            
            if (!(approvalTypeList.includes(document?.tasktype))) return setPageStatus('forbidden');

            setRequestDoc(document);
            setPageStatus('onload');

        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = DocumentListener('requests', requestid, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        }

    // eslint-disable-next-line
    }, [pageStatus]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='Account-Container height-[100vh] overflow-hidden'>

            {/* Main Page */}
            <div className='Account-Body'>
                <PageComponent
                    status={pageStatus}
                    header={
                        <div className='flex flex-col p-4  bg-neutral-100 h-28 w-full border-b border-b-neutral-300 border-1' hidden={pageStatus !== 'onload'}>
                            <BreadCrumbs rootLinkName={'My Approvals'} rootLinkRoute={'/approvals'} childLinkName={requestDoc?.parentrequestid?.toUpperCase()}></BreadCrumbs>
                            <h4 className='flex items-center h-full cursor-default text-[22px]'>{requestDoc?.taskname}</h4>
                        </div>
                    }
                    body={
                        <div className={`Request-Layout ${requestDoc?.status === 'Completed' || requestDoc?.status === 'Rejected' ? 'Request-Actioned' : ''}`}>

                            {/* REQUEST INFORMATION */}
                            <div className='Request-Summary h-[calc(100vh-225px)] overflow-y-auto'>

                                <div className='flex flex-col gap-6 p-8'>

                                    {/* Name */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Name</label>
                                        <p className='m-0'>{requestDoc?.taskname}</p>
                                    </div>

                                    {/* Description */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Short Description</label>
                                        <p className='m-0'>{requestDoc?.taskdescription}</p>
                                    </div>

                                    {/* Comment - Only visible in Final Approval */}
                                    <div className='flex flex-col gap-3' hidden={!['Final Approval', 'Finance Approval', 'COO Approval', 'LoA Approval'].includes(requestDoc?.tasktype)}>
                                        <label className='font-semibold'>Comment</label>
                                        <p className='m-0'>{requestDoc?.finalapprovalcomment}</p>
                                    </div>

                                    {/* Assigned To */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Assigned To</label>
                                        <div className='flex flex-col gap-1'>
                                            <p className='m-0' hidden={requestDoc?.assignedto?.email === 'Unassigned'}>
                                                {requestDoc?.assignedto?.givenname} {requestDoc?.assignedto?.surname}
                                            </p>
                                            <p className={`m-0 text-[#424242] ${requestDoc?.assignedto?.email !== 'Unassigned' && 'text-sm'}`}>
                                                {requestDoc?.assignedto?.email}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Status</label>
                                        <StatusLabel status={requestDoc?.status} tasktype={requestDoc?.tasktype}></StatusLabel>
                                    </div>

                                    {/* Created Date */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Created Date</label>
                                        <p className='m-0'>{ConvertDate(requestDoc?.createddate)}</p>
                                    </div>

                                    {/* Uploaded Documents */}

                                    {/* - Supporting Documents (Demand Management Approvals) */}
                                    {
                                        (requestDoc?.supportingdocs?.length > 0 || requestDoc?.supportingdocs !== undefined) &&
                                        <div className='flex flex-col gap-3' hidden={requestDoc?.supportingdocs?.length === 0 || requestDoc?.supportingdocs === undefined}>
                                            <label className='font-semibold'>Uploaded Documents</label>
                                            <div className='flex flex-row gap-2 items-center border-1 border-[#D8D8D8] px-3 py-2 rounded-md'>
                                                <img src={FileIcon} alt='file'></img>
                                                <a href={requestDoc?.supportingdocs[0]?.fileUrl} target='_blank' rel='noopener noreferrer'>{requestDoc?.supportingdocs[0]?.fileName}</a>
                                            </div>
                                        </div>
                                    }

                                    {/* - Attached Files (Quote Reviews) */}
                                    {
                                        (requestDoc?.attachedfiles?.length > 0 || requestDoc?.attachedfiles !== undefined) &&
                                        <div className='flex flex-col gap-3' hidden={requestDoc?.attachedfiles?.length === 0 || requestDoc?.attachedfiles === undefined}>
                                            <label className='font-semibold'>Uploaded Documents</label>
                                            <div className='flex flex-row gap-2 items-center border-1 border-[#D8D8D8] px-3 py-2 rounded-md'>
                                                <img src={FileIcon} alt='file'></img>
                                                <a href={requestDoc?.attachedfiles[0]?.fileUrl} target='_blank' rel='noopener noreferrer'>{requestDoc?.attachedfiles[0]?.fileName}</a>
                                            </div>
                                        </div>
                                    }

                                </div>

                                <hr className='text-neutral-500 p-0 m-0'></hr>


                                {/* VIEW MORE DETAILS */}
                                <div className='flex flex-row gap-3 px-3 py-2 items-center justify-between cursor-pointer' onClick={() => setViewDetails(!viewDetails)}>

                                    {/* Title */}
                                    <div className='flex flex-row items-center gap-2 py-2'>
                                        <img className='cursor-pointer' src={viewDetails ? Expand : Collapse} alt={'>'}></img>
                                        <h4 className='text-[16px] m-0'> {viewDetails ? 'View Less Details' : 'View More Details'} </h4>
                                    </div>

                                </div>

                                {/* Tab View of Supporting Information */}
                                <div className='flex flex-row justify-between pb-4' hidden={!viewDetails}>
                                    <FormRenderer task={requestDoc}></FormRenderer>
                                </div>

                                <hr className='text-neutral-500 p-0 m-0'></hr>

                                {/* TRACKING HISTORY */}
                                <TabView
                                    defaultView={1}
                                    oneTitle={'Tracking History'}
                                    oneContent={
                                        <div className='px-2'>
                                            <ActivityFeed
                                                task={requestDoc}
                                            ></ActivityFeed>
                                        </div>
                                    }
                                ></TabView>

                            </div>

                            {/* CHANGE REQUEST STATUS */}
                            <div className='Manage-Request flex flex-col justify-between overflow-y-auto h-[calc(100vh-242px)]'>

                                <div>
                                    <label className='p-4 font-semibold text-lg border-b-neutral-300 border-b w-full'>Approval Status</label>

                                    <div className='flex flex-col p-4 gap-4'>

                                        <label className='font-semibold'> Change status:</label>

                                        <select 
                                            className='Input-Field-Select' 
                                            value={requestStatus} 
                                            disabled={
                                                requestDoc?.status === 'Completed' ||
                                                requestDoc?.status === 'Rejected' || 
                                                requestDoc?.status === 'Cancelled' || 
                                                requestDoc?.assignedto?.email !== getUser?.emailaddress
                                            } 
                                            onChange={(e) => setRequestStatus(e.target.value)}
                                        >

                                            {/* If completed, show as "Approved", otherwise show "Rejected". If pending approval, show "Select" */}
                                            <option hidden value=''>{(requestDoc?.status !== 'In Progress' && requestDoc?.status !== 'For Review') ? requestDoc?.status === 'Completed' ? 'Approved' : requestDoc?.status : 'Select'}</option>
                                            <option value='Approve'>Approve</option>
                                            <option value='Reassign'>Reassign</option>
                                            <option className='text-[#DE0000]' value='Reject'>Reject</option>

                                        </select>

                                        <div className='flex flex-col gap-3' hidden={requestStatus !== 'Reassign'}>
                                            <label className='font-semibold'> 
                                                Reassign request to: <span className='text-[#C4314B]'>*</span>
                                            </label>
                                            <UserSearchNative 
                                                selectedUser={searchData}
                                                setSelectedUser={setSearchData}
                                                errorMessage={undefined}
                                            ></UserSearchNative>                                   
                                        </div>

                                        {/* Only shows when an option is selected */}
                                        <div hidden={requestStatus.length === 0} className='flex flex-col gap-2'>

                                            {/* Change help text based off approval status */}
                                            {
                                                requestStatus === 'Approve' ?                       
                                                <label className='font-semibold'> 
                                                    Comment
                                                </label>
                                                :
                                                <div className='flex flex-col gap-2'>
                                                    <label className='font-semibold'> 
                                                        Comment <span className='text-[#C4314B]'>*</span>
                                                    </label>
                                                    <i className='pb-2' hidden={requestStatus !== 'Reject'}>Please enter a reason for your decision to reject the request.</i>
                                                </div>
                                  
                                            }
                                
                                            <textarea
                                                className='Input-Field-TextArea'
                                                placeholder={`Add a comment${requestStatus === 'Reassign' ? ' for the new approver...' : '...'}`}
                                                value={changeStatusComment}
                                                onChange={(e) => setChangeStatusComment(e.target.value)}
                                            ></textarea>
                                            
                                        </div>

                                    </div>
                                </div>

                                {/* TODO - fix sticky portion */}
                                <div className='sticky self-start top-0 border-t-neutral-300 border-t w-full py-3 px-4 flex flex-row gap-3'>

                                    <button className='Primary-Button' disabled={isSubmitDisabled()} onClick={() => handleSubmit()}>
                                        Proceed
                                    </button>

                                    <button 
                                        className='Secondary-Button' 
                                        onClick={() => {
                                            setRequestStatus('');
                                            setChangeStatusComment('');
                                            setSearchData({
                                                'newUserInput': '', // email of the selected user
                                                'newUserName': '', // fullname of the selected user
                                                'newGivenname': '', // givenname of the selected user
                                                'newSurname': '', // surname of the selected user
                                                'newJobTitle': '', // jobtitle of the selected user
                                                'searchResults': '',
                                                'queryMessage': '',
                                            });
                                        }}
                                        disabled={
                                            requestDoc?.status === 'Completed' ||
                                            requestDoc?.status === 'Rejected' || 
                                            requestDoc?.status === 'Cancelled' || 
                                            requestDoc?.assignedto?.email !== getUser?.emailaddress
                                        }
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>
                        </div>
                    }
                ></PageComponent>
            </div>

            {/* Side Menu */}
            <div className='Account-SideNav h-[calc(100vh-133px)]'>
                <SideNav></SideNav>
            </div>

        </div>
    )
}